import { useEffect, useState } from "react";
import { Globe, MonitorSmartphone, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const PAGE_NAMES = { "/": "Home", "/meal-prep": "Meal Prep", "/catering": "Catering", "/contact": "Contact", "/account": "My Account" };
const pageName = (p) => PAGE_NAMES[p] || (p.startsWith("/review/") ? "Review form" : p.startsWith("/payment/") ? "Payment result" : p);

const Stat = ({ label, views, visitors, testid }) => (
  <div className="rounded-2xl border border-inkline bg-cream p-5" data-testid={testid}>
    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted2">{label}</div>
    <div className="mt-2 font-display text-3xl font-bold text-ink">{visitors}</div>
    <div className="text-xs text-muted2">visitors · {views} page views</div>
  </div>
);

const Bars = ({ series }) => {
  const max = Math.max(1, ...series.map((d) => d.visitors));
  return (
    <div className="flex h-36 items-end gap-[3px]" data-testid="traffic-chart" aria-label="Visitors per day, last 30 days">
      {series.map((d) => (
        <div key={d.day} className="group relative flex-1">
          <div className="w-full rounded-t-sm bg-spice/80 transition-colors group-hover:bg-spice" style={{ height: `${Math.max(2, (d.visitors / max) * 100)}%` }} />
          <div className="pointer-events-none absolute -top-9 left-1/2 hidden -translate-x-1/2 whitespace-nowrap rounded-md bg-ink px-2 py-1 font-mono text-[10px] text-parchment group-hover:block">
            {d.day.slice(5)} · {d.visitors} visitors · {d.views} views
          </div>
        </div>
      ))}
    </div>
  );
};

const RankList = ({ title, icon: Icon, rows, render, empty, testid }) => (
  <div className="rounded-2xl border border-inkline bg-cream p-5" data-testid={testid}>
    <h3 className="flex items-center gap-2 font-display text-base font-semibold text-ink"><Icon size={16} className="text-spice" /> {title}</h3>
    <ul className="mt-3 space-y-2 text-sm">
      {rows.length === 0 && <li className="text-xs text-muted2">{empty}</li>}
      {rows.map((r) => (
        <li key={r.key} className="flex items-center justify-between gap-3">
          <span className="truncate text-ink/85">{render(r.key)}</span>
          <span className="shrink-0 font-mono text-xs text-muted2">{r.visitors} visitors · {r.views} views</span>
        </li>
      ))}
    </ul>
  </div>
);

/** Admin → Traffic: first-party page-view analytics (last 30 days). */
export const TrafficPanel = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/traffic").then((r) => setData(r.data)).catch((err) => toast.error(formatApiError(err)));
  }, []);

  if (!data) return <div className="rounded-2xl border border-inkline bg-cream p-8 text-center text-sm text-muted2">Loading…</div>;

  const dev = data.devices || {};
  const devTotal = (dev.mobile || 0) + (dev.desktop || 0);
  const conv = data.month.visitors ? ((data.paid_orders_30d / data.month.visitors) * 100).toFixed(1) : "0.0";

  return (
    <div className="space-y-4" data-testid="traffic-panel">
      <div className="grid gap-4 sm:grid-cols-3">
        <Stat label="Today" {...data.today} testid="traffic-today" />
        <Stat label="Last 7 days" {...data.week} testid="traffic-week" />
        <Stat label="Last 30 days" {...data.month} testid="traffic-month" />
      </div>
      <div className="rounded-2xl border border-inkline bg-cream p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 font-display text-base font-semibold text-ink"><TrendingUp size={16} className="text-spice" /> Visitors per day · last 30 days</h3>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted2" data-testid="traffic-conversion">{data.paid_orders_30d} paid orders · {conv}% of visitors</span>
        </div>
        <div className="mt-5"><Bars series={data.series} /></div>
        <div className="mt-2 flex justify-between font-mono text-[10px] text-muted2"><span>{data.series[0]?.day.slice(5)}</span><span>today</span></div>
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <RankList title="Most visited pages" icon={Globe} rows={data.top_pages} render={pageName} empty="No visits recorded yet." testid="traffic-pages" />
        <RankList title="Where visitors come from" icon={Globe} rows={data.top_referrers} render={(k) => k} empty="Direct visits only so far (typed URL, bookmarks, apps)." testid="traffic-referrers" />
        <div className="rounded-2xl border border-inkline bg-cream p-5" data-testid="traffic-devices">
          <h3 className="flex items-center gap-2 font-display text-base font-semibold text-ink"><MonitorSmartphone size={16} className="text-spice" /> Devices · 30 days</h3>
          {devTotal === 0 ? <p className="mt-3 text-xs text-muted2">No visits recorded yet.</p> : (
            <>
              <div className="mt-4 flex h-3 overflow-hidden rounded-full bg-ink/10">
                <div className="bg-spice" style={{ width: `${((dev.mobile || 0) / devTotal) * 100}%` }} />
                <div className="bg-saffron" style={{ width: `${((dev.desktop || 0) / devTotal) * 100}%` }} />
              </div>
              <div className="mt-3 space-y-1 text-sm text-ink/85">
                <div className="flex justify-between"><span><span className="mr-2 inline-block h-2.5 w-2.5 rounded-full bg-spice" />Phone</span><span className="font-mono text-xs text-muted2">{Math.round(((dev.mobile || 0) / devTotal) * 100)}%</span></div>
                <div className="flex justify-between"><span><span className="mr-2 inline-block h-2.5 w-2.5 rounded-full bg-saffron" />Computer / tablet</span><span className="font-mono text-xs text-muted2">{Math.round(((dev.desktop || 0) / devTotal) * 100)}%</span></div>
              </div>
            </>
          )}
        </div>
      </div>
      <p className="text-xs text-muted2">Counted on your own site — no cookies or third-party trackers. Admin pages are excluded. Data is kept for 180 days.</p>
    </div>
  );
};
